import { renderInline } from "@/components/fund/fund-details-table";
import { BRAND } from "@/config/brand";
import { useFundBySlug } from "@/hooks/use-fund-list";
import { Link, useLocation } from "react-router-dom";

// Static (non-fund) top-level paths under the etf-website tree. A flat
// `/{slug}` URL that matches one of these is NOT a fund detail. Listed
// once here so the slug-extraction regex doesn't have to enumerate the
// fund slug set (which changes whenever a new ETF launches).
const NON_FUND_TOP_LEVEL_PATHS = new Set<string>([
  "",
  "products",
  "company",
  "blog",
  "privacy",
  "terms",
  "social-media-disclaimer",
  "glossary",
  "learn",
  "thematic",
  "buffered",
  "2xleveraged",
  "factsheets",
  "fund",
]);

/**
 * Match a fund-detail pathname and pull the slug. Returns `null` for any
 * non-fund-detail route.
 *
 * Fund-detail URL space (per the router):
 *   - /{slug}                            (canonical)
 *   - /fund/{slug}                       (legacy alias)
 *   - /buffered/{slug}                   (legacy)
 *   - /2xleveraged/{slug}                (legacy)
 *   - /thematic/{slug}                   (legacy)
 *
 * The legacy `/{category}/{slug}` forms are matched here because the
 * redirect is client-side — the footer renders before the navigate fires,
 * and we want fund-specific disclosures on first paint.
 */
function slugFromPathname(pathname: string): string | null {
  // Legacy `/{category}/{slug}` and `/fund/{slug}` — still emit the slug so
  // the disclosure layer is correct during the in-flight redirect.
  const legacy = pathname.match(/^\/(?:fund|buffered|2xleveraged|thematic)\/([^/?#]+)\/?$/);
  if (legacy?.[1]) return legacy[1];

  // Flat `/{slug}` — exactly one path segment, not a known non-fund page.
  const flat = pathname.match(/^\/([^/?#]+)\/?$/);
  const candidate = flat?.[1];
  if (!candidate) return null;
  if (NON_FUND_TOP_LEVEL_PATHS.has(candidate)) return null;
  return candidate;
}

type FooterLink = { label: string; to: string };

const footerColumns: { title: string; links: readonly FooterLink[] }[] = [
  {
    title: "Products",
    links: [
      { label: "Thematic ETFs", to: "/products?category=thematic" },
      { label: "2x Leveraged ETFs", to: "/products?category=leveraged" },
      { label: "Buffer ETFs", to: "/products?category=buffer" },
      { label: "Fixed Income ETFs", to: "/products?category=fixed" },
    ],
  },
  {
    title: "Company",
    links: [{ label: "About Corgi", to: "/company" }],
  },
  {
    title: "Resources",
    links: [
      { label: "ETF Glossary", to: "/glossary" },
      { label: "Insights & Blog", to: "/blog" },
      { label: "Contact", to: "/" },
    ],
  },
  {
    title: "Legal",
    links: [
      { label: "Privacy Policy", to: "/privacy" },
      { label: "Terms of Use", to: "/terms" },
      { label: "Social Disclaimer", to: "/social-media-disclaimer" },
    ],
  },
] as const;

const disclaimers = [
  "Investors should consider the investment objectives, risks, charges, and expenses of each Fund carefully before investing. This and other important information is contained in the prospectus for each Fund, which can be obtained without charge from corgifunds.com or from the SEC at www.sec.gov. Read the applicable prospectus carefully before investing.",
  "The Funds are newly organized and have limited operating history. There can be no assurance that the Funds will grow to or maintain an economically viable size. It may take time for the Funds to attract sufficient assets and for an active secondary market for their shares to develop or be sustained, which could result in wider bid-ask spreads, increased trading costs, or trading at a premium or discount to net asset value.",
  "The information on this site is for informational purposes only and does not constitute investment, tax, or legal advice. Please consult your own investment, tax, and legal professionals regarding your specific situation.",
  "Investing involves risk, including possible loss of principal. There is no guarantee that any investment strategy or any Fund will achieve its objectives. Shares of the Funds are bought and sold on an exchange at market price and are not individually redeemable from the Funds. Market price will fluctuate, sometimes materially, and may be higher or lower than net asset value (“NAV”). Brokerage commissions, bid-ask spreads and other trading costs will reduce returns. Performance data represents past performance and does not guarantee future results. Investment return and principal value will fluctuate so that shares, when sold, may be worth more or less than their original cost. Current performance may be lower or higher than the performance data quoted.",
  "The Fund issues and redeems shares only in large blocks called “Creation Units” at NAV next determined after an order is accepted. Only authorized participants (“APs”) may transact in Creation Units directly with the Fund. Investors should contact their broker or financial intermediary to place trades.",
  "This site is intended only for investors resident in the United States. Nothing on this website is an offer to sell, or a solicitation of an offer to buy, any security in any jurisdiction where such offer or solicitation would be unlawful.",
  "Corgi ETF Trust I, Corgi ETF Trust II, and Corgi ETF Trust III. Investment adviser: Corgi Strategies, LLC. Distributor: Paralel Distributors LLC. Member Firm. Paralel is unaffiliated with Corgi Strategies, LLC, The Corgi Company. \u00A9 2026 Corgi Strategies, LLC. All rights reserved.",
];

export function EtfFooter() {
  // Disclosure source-of-truth depends on the current route:
  //   - Non-fund routes (homepage, glossary, learn, products listings, etc.)
  //     show the generic site-wide `disclaimers` block.
  //   - Fund-detail routes show that fund's own `fund.disclosures` string
  //     (which already covers the generic boilerplate AND fund-specific risk
  //     paragraphs) rendered in the footer-bottom — REPLACING the generic
  //     disclaimers on those routes (same REPLACE strategy as Dane canonical).
  const { pathname } = useLocation();
  const slug = slugFromPathname(pathname);
  // Reuse the shared ["funds"] cache — no extra round-trip if the list has
  // already loaded (e.g. user came from home/products). Async: undefined
  // while loading → footer falls back to generic disclaimers during that
  // window, which is invisible to the user.
  const { data: ddbFund } = useFundBySlug(slug);
  // On a fund route where the fund has disclosures: render them in place of
  // the generic disclaimers. If the fund has no disclosures or hasn't
  // resolved yet, fall back to the generic block.
  const isOnFundPage = !!ddbFund;
  // Raw DDB disclosures string — may be plain `\n\n`-separated paragraphs,
  // or an HTML blob. Empty string / null → no per-fund disclosures.
  const fundDisclosuresRaw = ddbFund?.disclosures ?? "";
  const hasFundDisclosures = isOnFundPage && fundDisclosuresRaw.length > 0;
  // Paragraph style mirrors Dane's `fundFooterParagraphClass` per fund type.
  const isBufferFund = ddbFund?.fundType === "buffer";
  const fundFooterParagraphClass = isBufferFund
    ? "text-[13px] leading-[1.85] text-[var(--etf-white)]"
    : "text-[12px] leading-[1.8] text-[var(--etf-white3)]";
  const linkClass =
    "text-sm font-[family-name:var(--font-instrument)] text-[var(--etf-white3)] no-underline tracking-[0.02em] hover:text-[var(--etf-white2)] transition-colors";
  return (
    <footer className="bg-[var(--etf-black2)] border-t border-[var(--etf-border)]">
      {/* Top section: brand + columns */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-[1.5fr_1fr_1fr_1fr_1fr] gap-10 px-10 pt-16 pb-12 max-md:px-4 max-md:pt-12 max-md:pb-12 max-md:gap-10 max-[480px]:px-3 max-[480px]:pt-8 max-[480px]:gap-6">
        <div className="min-w-[200px] max-w-[280px]">
          <Link to="/" className="flex items-center mb-4 no-underline">
            <img
              src={BRAND.logos.dark}
              alt="Corgi Funds"
              className="h-7 w-auto block dark-only-logo"
              loading="lazy"
            />
            <img
              src={BRAND.logos.light}
              alt="Corgi Funds"
              className="h-7 w-auto block light-only-logo"
              loading="lazy"
            />
          </Link>
          <p className="text-sm text-[var(--etf-white3)] leading-[1.7] font-[family-name:var(--font-instrument)]">
            ETFs designed to offer direct access to disruptive themes and companies shaping the
            future.
          </p>
        </div>

        {footerColumns.map((col) => (
          <div key={col.title}>
            <div className="text-xs font-[family-name:var(--font-instrument)] tracking-[0.16em] uppercase text-[var(--etf-white2)] mb-4">
              {col.title}
            </div>
            <div className="flex flex-col gap-2.5">
              {col.links.map((link) => (
                <Link key={link.label} to={link.to} className={linkClass}>
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Bottom disclosures — per-fund on fund-detail routes (REPLACE strategy),
          generic site-wide disclaimers everywhere else. See `slugFromPathname`. */}
      <div
        className="px-10 pt-8 pb-12 border-t border-[var(--etf-border2)] font-[family-name:var(--font-instrument)] max-md:px-4 max-md:pb-8 max-[480px]:px-3 max-[480px]:pb-6"
        data-testid="etf-footer-disclosures"
      >
        {hasFundDisclosures ? (
          // Fund-detail route: render per-fund disclosures in place of the
          // generic disclaimers. REPLACE strategy (not prepend/append) mirrors
          // Dane canonical — per-fund blocks already include all mandatory
          // boilerplate inline, so appending the generic array would duplicate.
          <>
            <p className="text-[11px] tracking-[0.16em] uppercase text-[var(--etf-white2)] mb-4">
              Disclosures
            </p>
            {/^\s*</.test(fundDisclosuresRaw) ? (
              // HTML blob — admin-authored HTML (e.g. fixed-income funds).
              <div
                className={fundFooterParagraphClass}
                // biome-ignore lint/security/noDangerouslySetInnerHtml: admin-authored HTML from DDB disclosures field
                dangerouslySetInnerHTML={{ __html: fundDisclosuresRaw }}
              />
            ) : (
              // Plain text with optional `**bold**`/`_italic_` markers.
              fundDisclosuresRaw
                .split("\n\n")
                .map((chunk) => chunk.trim())
                .filter(Boolean)
                .map((para, i, arr) => (
                  <p
                    key={para.slice(0, 48)}
                    className={`${fundFooterParagraphClass}${i < arr.length - 1 ? " mb-3.5" : ""}`}
                  >
                    {renderInline(para)}
                  </p>
                ))
            )}
          </>
        ) : (
          // Non-fund route (or fund not yet resolved): generic site-wide disclaimers.
          disclaimers.map((text, i) => (
            <p
              key={text.slice(0, 48)}
              className={`text-[12px] leading-[1.8] text-[var(--etf-white3)] ${i === 0 ? "italic" : ""} ${i < disclaimers.length - 1 ? "mb-3.5" : ""}`}
            >
              {text}
            </p>
          ))
        )}
      </div>
    </footer>
  );
}
